<template>
  <v-dialog :model-value="isVisible" persistent width="600" @close="closeModal">
    <v-form ref="form">
      <v-card>
        <v-card-title class="d-flex flex-row">
          <h4 class="text-capitalize">
            <v-icon size="small" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span>
              {{ t('yard.dialogs.yardViewAssetContainerCreateDialog.headers.createAssetTestOnly') }}
            </span>
          </h4>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeModal"></v-btn>
        </v-card-title>
        <v-card-text class="d-flex flex-column gap-4">
          <p>
            {{
              t(
                'yard.dialogs.yardViewAssetContainerCreateDialog.messages.thisToolCreatesAnAssetForTestingPurposesItWillAddAnAssetToTheWaitingListForThisYard'
              )
            }}
          </p>

          <div class="d-flex flex-column my-4">
            <br />
            <h4>{{ t('yard.dialogs.yardViewAssetContainerCreateDialog.headers.trailerData') }}</h4>
            <br />

            <v-text-field
              color="primary"
              :label="t('yard.dialogs.yardViewAssetContainerCreateDialog.labels.trailerCode')"
              v-model="assetContainerData.code"
              :placeholder="
                t(
                  'yard.dialogs.yardViewAssetContainerCreateDialog.placeholders.enterTheTrailerIdentifier'
                )
              "
              required
              :rules="
                $validator.rules.required(
                  t('yard.dialogs.yardViewAssetContainerCreateDialog.labels.trailerCode')
                )
              "
              variant="outlined"
              class="flex-grow-1"></v-text-field>
            <v-select
              color="primary"
              :label="t('yard.dialogs.yardViewAssetContainerCreateDialog.labels.trailerType')"
              density="compact"
              v-model="assetContainerData.type"
              :rules="
                $validator.rules.required(
                  t('yard.dialogs.yardViewAssetContainerCreateDialog.labels.trailerType')
                )
              "
              required
              variant="outlined"
              :placeholder="
                t(
                  'yard.dialogs.yardViewAssetContainerCreateDialog.placeholders.selectTheTrailerType'
                )
              "
              :items="assetContainerTypes"
              item-title="title"
              item-value="value"
              class="flex-grow-1"></v-select>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="closeModal">{{ t('global.buttons.nevermind') }}</v-btn>
          <v-btn
            class="bg-primary"
            @click="createAssetContainer"
            :disabled="assetContainerData.code?.length <= 1">
            {{ t('global.buttons.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { AssetContainerType } from '@nova/core';
import { isFormValid } from '@satellite/plugins/util.js';
import { useNotify } from '@/composables';
import useYard from '@/modules/yard/composables/useYard';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetContainerCreateDialog',
  emits: ['close'],
  props: {
    warehouseId: {
      type: String,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: true
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const form = ref(null);

    const assetContainerData = ref({ code: '', type: AssetContainerType.Trailer });

    const closeModal = () => {
      emit('close');

      // Reset form values
      assetContainerData.value = { code: '', type: AssetContainerType.Trailer };
    };

    const notify = useNotify();
    const yardStore = useYard();

    const assetContainerTypes = Object.keys(AssetContainerType).map(key => ({
      title: key,
      value: AssetContainerType[key]
    }));

    const createAssetContainer = async () => {
      if (isFormValid(form.value)) {
        try {
          yardStore.setIsLoadingAssetVisits(true);
          // IMPORTANT: This is just for testing purposes
          const payload = {
            warehouseId: props.warehouseId,
            code: assetContainerData.value.code,
            type: assetContainerData.value.type,
            notes: 'Created by the yard view test tool'
          };

          await axios.post('asset-container', payload);

          yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.CREATE_ASSET_CONTAINER, {
            entryPoint: 'Manual Add Asset',
            metadata: {
              asset_container_count: 1,
              asset_container_type: assetContainerData.value.type,
              asset_container_code: assetContainerData.value.code
            }
          });

          notify(
            t(
              'yard.dialogs.yardViewAssetContainerCreateDialog.messages.assetContainerCreatedSuccessfully'
            ),
            'success'
          );
          yardStore.fetchAssetVisits();
        } catch (error) {
          notify(
            `${t(
              'yard.dialogs.yardViewAssetContainerCreateDialog.messages.failedToCreateAssetContainer'
            )}: ${error}`,
            'error'
          );
        } finally {
          yardStore.setIsLoadingAssetVisits(false);
          closeModal();
        }
      } else {
        notify(
          t(
            'yard.dialogs.yardViewAssetContainerCreateDialog.messages.pleaseFillInAllRequiredFields'
          ),
          'error'
        );
      }
    };

    return {
      t,
      assetContainerData,
      assetContainerTypes,
      AssetContainerType,
      createAssetContainer,
      closeModal,
      form
    };
  }
});
</script>
